import { useEffect, useState } from "react";
import NotFound from "./NotFound";
import { saDate, publicName } from "../lib/format";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Calendar, Banknote, Users, Clock, Briefcase, ArrowLeft, Send, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import SEO from "../components/SEO";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [proposedRate, setProposedRate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  useEffect(() => {
    if (job && user) {
      if (user.user_id === job.client_id) {
        fetchApplications();
      } else {
        checkApplied();
      }
    }
  }, [job, user]);

  const fetchJob = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}`);
      setJob(r.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/applications`, { withCredentials: true });
      setApplications(r.data);
    } catch {
      setApplications([]);
    }
  };

  const checkApplied = async () => {
    try {
      const r = await axios.get(`${API}/my-applications`, { withCredentials: true });
      setHasApplied(r.data.some((a) => a.job_id === jobId));
    } catch {
      setHasApplied(false);
    }
  };

  const submitApplication = async () => {
    if (!user) {
      toast.error("Please log in to apply");
      navigate("/login");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/jobs/${jobId}/apply`,
        {
          cover_letter: coverLetter || null,
          proposed_rate: proposedRate ? parseFloat(proposedRate) : null,
        },
        { withCredentials: true }
      );
      toast.success("Application submitted!");
      setShowApply(false);
      setHasApplied(true);
      fetchJob();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to apply"));
    } finally {
      setSubmitting(false);
    }
  };

  const updateAppStatus = async (app_id, status) => {
    try {
      await axios.put(`${API}/applications/${app_id}`, { status }, { withCredentials: true });
      toast.success(`Application ${status}`);
      fetchApplications();
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) return <NotFound />;

  const isOwner = user?.user_id === job.client_id;

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="job-detail-page">
      <SEO url={`/jobs/${jobId}`} title={job.title} description={job.description?.slice(0, 150)} />

      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate("/jobs")}
          className="flex items-center gap-2 text-zinc-500 hover:text-orange-500 mb-6"
          data-testid="back-to-jobs-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Jobs
        </button>

        <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div className="flex-1">
              <h1 className="font-heading text-3xl md:text-4xl text-white" data-testid="job-title">
                {job.title}
              </h1>
              <div className="flex items-center gap-3 mt-3 text-sm text-zinc-500">
                <span>Posted by <span className="text-zinc-300">{publicName(job.client_name)}</span></span>
                <span>·</span>
                <span>{saDate(job.created_at)}</span>
                <Badge className="bg-zinc-800 text-zinc-300 uppercase">{job.status}</Badge>
              </div>
            </div>
            {!isOwner && job.status === "open" && (
              <Button
                onClick={() => (user ? setShowApply(true) : navigate("/login"))}
                disabled={hasApplied}
                className="bg-orange-600 hover:bg-orange-500"
                data-testid="apply-job-btn"
              >
                {hasApplied ? (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Applied</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Apply Now</>
                )}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-zinc-800 text-sm">
            {(job.budget_min || job.budget_max) && (
              <span className="flex items-center gap-2 text-orange-400">
                <Banknote className="h-4 w-4" />
                {job.budget_min && job.budget_max
                  ? `R${job.budget_min.toLocaleString()} – R${job.budget_max.toLocaleString()}`
                  : `R${(job.budget_min || job.budget_max).toLocaleString()}`}
              </span>
            )}
            {job.location && (
              <span className="flex items-center gap-2 text-zinc-400"><MapPin className="h-4 w-4" />{job.location}</span>
            )}
            {job.duration && (
              <span className="flex items-center gap-2 text-zinc-400"><Clock className="h-4 w-4" />{job.duration}</span>
            )}
            {job.deadline && (
              <span className="flex items-center gap-2 text-zinc-400"><Calendar className="h-4 w-4" />Deadline {saDate(job.deadline)}</span>
            )}
            <span className="flex items-center gap-2 text-zinc-500"><Users className="h-4 w-4" />{job.application_count || 0} applicants</span>
          </div>

          <div className="prose prose-invert max-w-none mb-6">
            <h3 className="font-heading text-xl text-white mb-3">Description</h3>
            <p className="text-zinc-300 whitespace-pre-wrap">{job.description}</p>
          </div>

          {job.skills_required?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-xl text-white mb-3">Skills Required</h3>
              <div className="flex flex-wrap gap-2">
                {job.skills_required.map((s) => (
                  <Badge key={s} variant="outline" className="border-zinc-700 text-zinc-300">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Owner sees applications */}
        {isOwner && (
          <div className="mt-8" data-testid="applications-section">
            <h2 className="font-heading text-2xl uppercase mb-4">Applications ({applications?.length || 0})</h2>
            {applications?.length > 0 ? (
              <div className="space-y-3">
                {applications.map((a) => (
                  <div key={a.application_id} className="bg-zinc-900 border border-zinc-800 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex gap-3 flex-1">
                        <Link to={`/profile/user/${a.applicant_id}`} className="w-12 h-12 rounded-full bg-zinc-800 overflow-hidden flex-shrink-0">
                          {a.applicant_picture ? (
                            <img src={a.applicant_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500">{a.applicant_name?.[0]}</div>
                          )}
                        </Link>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Link to={`/profile/user/${a.applicant_id}`} className="font-medium text-white hover:text-orange-400">{a.applicant_name}</Link>
                            <Badge className={`text-xs ${a.status === 'accepted' ? 'bg-emerald-900/40 text-emerald-300' : a.status === 'rejected' ? 'bg-red-900/40 text-red-300' : 'bg-yellow-900/40 text-yellow-300'}`}>{a.status}</Badge>
                          </div>
                          {a.proposed_rate && (
                            <p className="text-sm text-orange-400 mt-1">Rate: R{a.proposed_rate.toLocaleString()}</p>
                          )}
                          {a.cover_letter && (
                            <p className="text-sm text-zinc-400 mt-2 whitespace-pre-wrap">{a.cover_letter}</p>
                          )}
                        </div>
                      </div>
                      {a.status === "pending" && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => updateAppStatus(a.application_id, "accepted")} className="bg-emerald-700 hover:bg-emerald-600">Accept</Button>
                          <Button size="sm" variant="outline" onClick={() => updateAppStatus(a.application_id, "rejected")} className="border-zinc-700">Reject</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
                <Users className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No applications yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={showApply} onOpenChange={(o) => !o && setShowApply(false)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Apply to this job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300">Cover letter</Label>
              <Textarea
                className="bg-zinc-900 border-zinc-800 mt-1 min-h-32"
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Why you're a great fit…"
                data-testid="cover-letter-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Your rate (ZAR, optional)</Label>
              <Input
                className="bg-zinc-900 border-zinc-800 mt-1"
                type="number"
                value={proposedRate}
                onChange={(e) => setProposedRate(e.target.value)}
                placeholder="5000"
                data-testid="proposed-rate-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApply(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={submitApplication} disabled={submitting} className="bg-orange-600 hover:bg-orange-500" data-testid="confirm-apply-btn">
              {submitting ? "Submitting…" : "Submit Application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
